
import styled, { css } from 'styled-components';
import { navy, grey25, grey10, white } from '@zocdoc/branding/colors';
import {
    ContentPrimitives as APContentPrimitives,
    ControlPrimitives as APControlPrimitives,
    ViewConstants as APViewConstants,
} from '@zocdoc/components/src/AppointmentPicker';
import { breakpoint } from '@zocdoc/components/src/grid';
import { Alert, InvertedCheck } from '@zocdoc/components/src/icons';
import { PaddedContainer } from '@zocdoc/components/src/Layout';
import { FontFamily } from '@zocdoc/components/src/typography';
import { Sticky } from '@zocdoc/react-sticky-5';
import {
    HEADER_BAR_HEIGHT_XXSMALL,
    HEADER_BAR_HEIGHT_MEDIUM,
    RESULT_CARD_X_MARGIN_MEDIUM,
    RESULT_CARD_X_MARGIN_LARGE,
} from '../../constants';
import { PROVIDER_RESULT_TYPES } from './constants';

export const SearchResultsNetworkBar = styled(PaddedContainer)`
    background-color: ${white};
    border-bottom: 1px solid ${grey10};
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    max-width: 100vw;

    ${breakpoint('small', { down: true })} {
        padding: 0;
        flex-direction: column-reverse;
    }

    ${breakpoint.medium} {
        align-items: center;
        flex-direction: row;
        padding-right: ${RESULT_CARD_X_MARGIN_MEDIUM}px;
    }

    ${breakpoint.large} {
        padding-right: ${RESULT_CARD_X_MARGIN_LARGE}px;
    }
`;

export const SearchResultsNetworkBarNetworkType = styled(PaddedContainer)`
    cursor: pointer;
    align-items: center;
    color: ${navy};
    display: flex;
    font: 15px/1 ${FontFamily.SharpSansSemibold};
    height: ${HEADER_BAR_HEIGHT_XXSMALL}px;
    order: 2;

    ${props => props['data-type'] === PROVIDER_RESULT_TYPES.NON_INSURANCE && css`
        ${breakpoint('small', { down: true })} {
            display: none !important;
        }
    `}

    ${breakpoint.small} {
        background-color: ${white};
        justify-content: flex-start;
    }

    ${breakpoint.medium} {
        font-family: ${FontFamily.SharpSansBold};
        font-size: 18px;
        height: ${HEADER_BAR_HEIGHT_MEDIUM}px;
        order: 0;
        padding: 0;
    }
`;

export const SearchResultsNetworkBarContainer = styled(Sticky)`
    position: relative;
    z-index: 2;
`;

export const SearchResultsNetworkBarInsuranceImage = styled.img`
    margin-left: 10px;
    max-height: 30px;
    max-width: 50px;

    ${breakpoint.medium} {
        display: none;
    }
`;

export const Icon = styled.div`
    margin-right: 18px;

    ${breakpoint('small', { down: true })} {
        margin-right: 15px;
    }
`;

// the green used in the comps is super different than the styleguide, there's no close match
export const SearchResultsNetworkBarNetworkTypeCheckIcon = Icon.withComponent(InvertedCheck);
export const SearchResultsNetworkBarNetworkTypeCloseIcon = Icon.withComponent(Alert);

export const AP_PERCENTAGE_XXL = 45;
export const AP_SIZE_XL = 425;
export const AP_SIZE_L = 425;
export const AP_SIZE_M = 300;

export const NETWORK_BAR_DISPLAY_STYLE = 'flex';

// probably going to want to include more days at larger breakpoints
export const SearchResultsNetworkBarTimesControls = styled.div`
    align-items: center;
    border-bottom: 1px solid ${grey25};
    display: ${NETWORK_BAR_DISPLAY_STYLE};
    height: ${HEADER_BAR_HEIGHT_XXSMALL}px;
    margin: 0 !important; /* font mixin screws things up, so need to !important these */
    width: 100%;

    ${breakpoint('small', { down: true })} {
        ${APControlPrimitives.MobileWrapper} {
            background-color: ${p => p.theme.components.AppointmentPicker.header.bgColor};
            color: ${p => p.theme.components.AppointmentPicker.header.textColor};
        }
    }

    ${breakpoint('medium', { down: true })} {
        font: 14px ${FontFamily.SharpSansSemibold};
    }

    ${breakpoint.medium} {
        border: 0;
        margin-left: auto !important; /* font mixin screws things up, so need to !important these */
        height: ${HEADER_BAR_HEIGHT_MEDIUM}px;
        width: ${AP_SIZE_M}px;
    }

    ${breakpoint.large} {
        width: ${AP_SIZE_L}px;
    }

    ${breakpoint.xlarge} {
        width: ${AP_SIZE_XL}px;
    }

    ${breakpoint.xxlarge} {
        width: ${AP_PERCENTAGE_XXL}%;
    }
`;

export const SearchTimesGrid = styled.div`
    align-self: flex-start;
    max-width: 100%;
`;

export const SearchResultsNonBookableContainer = styled.div`
    display: flex;
    justify-content: space-around;
`;

export const SearchResultsNonBookableLink = styled.a`
    background: ${p => p.theme.search.results.timesGridOverrides.idle.bgColor};
    border-color: ${p => p.theme.search.results.timesGridOverrides.idle.borderColor};
    border: 1px solid;
    box-sizing: border-box;
    color: ${p => p.theme.search.results.timesGridOverrides.idle.textColor};
    font: 13px/18px ${FontFamily.SharpSansSemibold};
    padding: 5px 16px;
    text-align: center;
    width: 100%;

    ${breakpoint.medium} {
        width: ${APViewConstants.DESKTOP_WIDTH_MATCH_SLOT_AREA};
    }
`;

export const SearchResultsPreviewMessage = styled(APContentPrimitives.DesktopNonBookableMessage)`
    ${breakpoint.small} {
        justify-content: flex-start;
    }

    ${breakpoint.medium} {
        justify-content: center;
    }
`;

export const Section = styled.section``;

export const SearchResultsList = styled.div`
    flex: 1 1 auto;

    ${Section}:last-child *:last-child {
        margin-bottom: 0;
    }

    ${Section}:not(:first-child) {
        ${SearchResultsNetworkBarTimesControls} {
            display: none;
        }

        ${SearchResultsNetworkBarContainer}[style*="position: fixed"] {
            ${SearchResultsNetworkBarTimesControls} {
                display: ${NETWORK_BAR_DISPLAY_STYLE};
            }
        }
    }

    ${APContentPrimitives.DesktopWrapper},
    ${APContentPrimitives.MobileWrapper},
    ${APControlPrimitives.DesktopWrapper},
    ${APControlPrimitives.MobileWrapper} {
        opacity: ${p => p.ready ? 1 : 0};
        transition: opacity 300ms ease-in-out;
    }

    ${breakpoint.xsmall} {
        background: ${grey10};
    }
`;

export const DoctorInfoContainer = styled.div`
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;
    padding-top: 5px;

    ${breakpoint.medium} {
        padding-top: 0;
    }
`;

export const DoctorInfoTopBar = styled.div`
    align-items: flex-end;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

export const DoctorName = styled.div`
    box-sizing: border-box;
    color: ${navy};
    font: 16px/1.4 ${FontFamily.SharpSansBold};
    border-bottom: 1px dotted;

    &:hover {
        border-style: solid;
    }

    ${breakpoint.medium} {
        font-size: 20px;
    }
`;

export const DoctorSpecialty = styled.div`
    color: ${navy};
    font: 13px/1 ${FontFamily.SharpSansSemibold};
    margin-top: 7px;

    ${breakpoint.small} {
        font-size: 12px;
    }

    ${breakpoint.medium} {
        font-size: 15px;
        line-height: 1.333;
    }
`;

export const DoctorCardContainer = styled.div`
    cursor: pointer;
    display: flex;
    padding: 4px 0 0;
    position: relative;
    vertical-align: top;
`;
